import { useEffect, useState } from 'react';
import { useI18n } from './i18n';
import { getResearch, isError, type ResearchDocument } from './api';

/**
 * What the research pass actually found.
 *
 * **This was a gap, and it was the wrong one to have.** The screen announced that
 * research had finished and offered to build a curriculum from it, with no way to
 * see what "it" was — asking somebody to approve, and pay for, work built on
 * material they could not read.
 *
 * It matters more than a general principle about transparency. A research document
 * is explicit about what it could *not* find — the passes so far have both ended
 * with a "Not found" section naming the gaps, and in one case said outright that the
 * search budget ran out before it reached the tables the objective asked for. That
 * is the most decision-relevant thing in the document, and hiding the document hid
 * it.
 *
 * Collapsed by default: it is tens of thousands of words, and the review screen has
 * a decision on it that this must not bury.
 */
export default function Research({ objectiveId }: { objectiveId: string }) {
  const { t, locale } = useI18n();
  const [documents, setDocuments] = useState<ResearchDocument[] | undefined>();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let stale = false;
    void getResearch(objectiveId).then((result) => {
      // Guarded because switching objectives remounts this with a new id while the
      // previous request is still in flight — without it the old objective's
      // research can land on the new one's screen.
      if (stale) return;
      setDocuments(isError(result) ? [] : result.documents);
    });
    return () => {
      stale = true;
    };
  }, [objectiveId]);

  // Nothing yet, or nothing ever. Either way this section has nothing to say, and
  // an empty panel headed "what the research found" reads as a failure.
  if (!documents || documents.length === 0) return null;

  const when = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <section className="panel">
      <details className="history" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
        <summary>
          <h2>{t('res.title')}</h2>
          <span className="muted small">
            {documents.reduce((total, doc) => total + doc.characters, 0).toLocaleString(locale)}{' '}
            {t('res.characters')}
          </span>
        </summary>

        <p className="muted">{t('res.hint')}</p>

        {documents.map((doc) => (
          <article key={doc.key}>
            <p className="meta small">
              {when.format(new Date(doc.producedAt))} · {doc.origin} ·{' '}
              {doc.characters.toLocaleString(locale)} {t('res.characters')}
            </p>
            {/* Deliberately raw, in a scrolling block, rather than rendered as
                Markdown. The document's value here is that it is the exact text the
                curriculum was built from — including the tables and the inline
                URLs — and a renderer that swallowed a malformed table or a citation
                would be hiding the thing worth checking. */}
            <pre className="extracted research">
              {doc.body}
              {doc.truncated ? `\n\n… ${t('res.truncated')}` : ''}
            </pre>
          </article>
        ))}
      </details>
    </section>
  );
}
